import React from 'react';
import { useTenant } from '../contexts/TenantContext';
import { tenantUrl } from '../utils/tenantHost';
import { getTenantLogo } from '../utils/tenantBranding';
import { Icon, IconBox, PageHero, Pill, SectionHeader } from '../components/ui';

// Shown on the root domain (e.g. https://trimedcare.in), where no tenant is
// selected. Each clinic lives on its own subdomain, so this page only describes
// the platform and links out to the clinics.

const FEATURES = [
  { icon: 'video',    tone: 'cyan',   title: 'Online consultations',  body: 'Book video or in-clinic appointments with verified doctors and get consultation notes online.' },
  { icon: 'package',  tone: 'orange', title: 'Pharmacy',              body: 'Order medicines from your clinic\'s pharmacy, with prescription upload and order tracking.' },
  { icon: 'calendar', tone: 'indigo', title: 'Smart scheduling',      body: 'Doctors publish their availability; patients pick an open slot in a few taps.' },
  { icon: 'rx',       tone: 'violet', title: 'Digital prescriptions', body: 'Prescriptions and active medicines are kept in one place for every patient.' },
  { icon: 'file',     tone: 'pink',   title: 'Health insights',       body: 'Articles, videos and whitepapers written by the clinic\'s own doctors.' },
  { icon: 'building', tone: 'mint',   title: 'Built for clinics',     body: 'Every clinic gets its own address and fully separate data.' },
];

const initials = (name = '') =>
  name.split(' ').filter(Boolean).slice(0, 2).map((w) => w[0].toUpperCase()).join('');

const ClinicCard = ({ clinic }) => {
  const url  = tenantUrl(clinic.subdomain);
  const logo = getTenantLogo(clinic.subdomain);
  return (
    <a href={url} className="ds-clinic">
      <div className="ds-clinic-brand">
        {logo
          ? <img src={logo} alt={clinic.name} className="ds-clinic-logo" />
          : <IconBox tone="indigo" size="lg"><span style={{ fontFamily: 'Sora, sans-serif', fontWeight: 700, fontSize: 18 }}>{initials(clinic.name)}</span></IconBox>}
      </div>
      <div className="ds-clinic-body">
        <h3 className="ds-clinic-name">{clinic.name}</h3>
        {clinic.address && (
          <p className="ds-clinic-address">
            <Icon name="mapPin" size={13} />
            <span>{clinic.address}</span>
          </p>
        )}
        <div className="ds-clinic-foot">
          <span className="ds-clinic-host">{new URL(url).host}</span>
          <span className="ds-btn ds-btn--sm">Visit clinic <Icon name="arrowRight" size={14} /></span>
        </div>
      </div>
    </a>
  );
};

const PlatformLanding = () => {
  const { tenants } = useTenant();
  const clinics = tenants.filter((t) => t.subdomain);

  return (
    <div className="page">
      <nav className="navbar">
        <div className="navbar-inner">
          <a className="navbar-logo" href="/" style={{ textDecoration: 'none' }}>
            <div className="navbar-logo-icon">
              <svg viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2.5"
                strokeLinecap="round" strokeLinejoin="round" width="16" height="16">
                <polyline points="22 12 18 12 15 21 9 3 6 12 2 12"/>
              </svg>
            </div>
            <span className="navbar-logo-text">ArogyaOne</span>
          </a>
          <a href="#clinics" className="ds-btn ds-btn--sm">Find your clinic</a>
        </div>
      </nav>

      <main className="page-main">

        <PageHero
          size="xl" tall art="admin"
          badge={<Pill tone="indigo">For clinics and their patients</Pill>}
          title={<>Healthcare, <span className="gradient-text">under your clinic's name</span></>}
          subtitle="One platform for clinics and their patients: appointments, consultations, prescriptions and pharmacy, all under your clinic's own web address."
        >
          <a href="#clinics" className="ds-btn ds-btn--lg">
            Find your clinic <Icon name="arrowRight" size={15} />
          </a>
        </PageHero>

        <section className="fade-up-1">
          <SectionHeader title="Everything a clinic needs" subtitle="Each clinic runs on the same platform with its own patients, doctors and data." />
          <div className="ds-feature-grid">
            {FEATURES.map((f) => (
              <div key={f.title} className="ds-feature">
                <IconBox tone={f.tone}><Icon name={f.icon} size={20} /></IconBox>
                <h3 className="ds-service-title">{f.title}</h3>
                <p className="ds-service-desc">{f.body}</p>
              </div>
            ))}
          </div>
        </section>

        <section id="clinics" className="ds-section fade-up-2" style={{ scrollMarginTop: 'calc(var(--ds-header-h) + 16px)' }}>
          <SectionHeader
            title="Find your clinic"
            subtitle={<>Each clinic has its own address, such as <strong>yourclinic.trimedcare.in</strong>. Sign in there with the phone number registered at your clinic.</>}
          />

          {clinics.length === 0 ? (
            <div className="card" style={{ padding: 20 }}>
              <p className="t-body-sm" style={{ margin: 0 }}>
                Use the web address your clinic shared with you.
              </p>
            </div>
          ) : (
            <div className="ds-clinic-grid">
              {clinics.map((t) => <ClinicCard key={t.id} clinic={t} />)}
            </div>
          )}
        </section>

      </main>
    </div>
  );
};

export default PlatformLanding;
